import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Button, Form, Modal} from 'semantic-ui-react';
import {useDataInspector} from '../../providers/DataInspectorProvider';
import {useAuth} from "../../providers/AuthenticationProvider";
import {useNavigate, useLocation} from "react-router";
import {usePointMapping} from "../../providers/PointMappingProvider";
import {Grid, GridColumn} from "@progress/kendo-react-grid";
import {SemanticToastContainer, toast} from "react-semantic-toasts";
import moment from "moment";
import {pointMappingService} from "../../services/PointMappingService";
import {equipmentService} from "../../services/equipmentService";
import {buildNiagaraCsvFilename, buildNiagaraStationCsv, decodeSlotPath} from "./niagaraStationCsv";

const WEB_PAGE = 'Point Mapping';

const content = {
    marginTop: '2rem',
};

export const PointMapping = () => {
    const { checkAuth, getUserType } = useAuth();
    const { createNumericPoint } = useDataInspector();
    const { userType } = useAuth();

    const [canSeeAllColumns, setCanSeeAllColumns] = useState(false);
    const [groupId, setGroupId] = useState();
    const [groupOptions, setGroupOptions] = useState([]);
    const [buildingId, setBuildingId] = useState();
    const [buildingOptions, setBuildingOptions] = useState([]);
    const [equipmentId, setEquipmentId] = useState();
    const [equipmentOptions, setEquipmentOptions] = useState([]);
    const [selectedNumericPoint, setSelectedNumericPoint] = useState(null);
    const [numericPointTimestamp, setNumericPointTimestamp] = useState(moment().format('YYYY-MM-DDTHH:mm'));
    const [numericPointValue, setNumericPointValue] = useState(0);
    const [selectedPoint, setSelectedPoint] = useState(null);
    const [selectedNiagaraPointId, setSelectedNiagaraPointId] = useState(null);
    const [niagaraStationPoints, setNiagaraStationPoints] = useState([]);
    const [mappedEquipmentPoints, setMappedEquipmentPoints] = useState([]);
    const [mappedPointsLoadFailed, setMappedPointsLoadFailed] = useState(false);
    const [mostRecentTimestamp, setMostRecentTimestamp] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    checkAuth(getUserType(), WEB_PAGE) && navigate('/');

    const {
        getDataInspectorGroups,
        getDataInspectorBuildings,
        getDataInspectorEquipment,
        groups,
        buildings,
        equipment
    } = useDataInspector();

    const {
        equipmentPoints,
        setEquipmentPoints,
        viewEquipmentPoints
    } = usePointMapping();

    useEffect(() => {
        getDataInspectorGroups();
    }, [getDataInspectorGroups]);

    useEffect(() => {
        if (location && location.state) {
            handleGroupChange(location.state.groupId);
            handleBuildingChange(location.state.buildingId);
            handleEquipmentChange(location.state.equipmentId);
        }
    }, [location]);

    useEffect(() => {
        if (userType === 'System Administrator' || userType === 'Pathian Technician')
        {
            setCanSeeAllColumns(true);
        } else {
            setCanSeeAllColumns(false);
        }
    }, [userType]);

    // Incremented on every group change. Both group-scoped fetches carry the value
    // they started with and drop their result if it no longer matches, so a slow
    // response for an earlier group cannot land after a faster one for the current
    // group. Without this, selecting Sequoia (3,805 points) then quickly Parkview
    // (210) leaves the page reading Parkview while state holds Sequoia - and the
    // export produces a complete, internally consistent file for the wrong station.
    const niagaraRequestRef = useRef(0);

    const isStaleRequest = (requestId) =>
        requestId !== null && requestId !== niagaraRequestRef.current;

    const clearNiagaraStationState = () => {
        setNiagaraStationPoints([]);
        setMostRecentTimestamp(null);
        setSelectedNiagaraPointId(null);
    };

    const getNiagaraStations = async (groupId, requestId = null) => {
        if (!groupId) {
            return;
        }

        let niagaraStations;
        try {
            const response = await pointMappingService.getNiagaraStations(groupId);
            if (isStaleRequest(requestId)) {
                return;
            }
            // The service swallows a 401 and returns null - that is a failure, not an
            // empty result. This page is granted to Pathian Engineer and Engineer, but
            // the Niagara endpoints allow only System Administrator and Pathian
            // Technician, so those roles land here on every group select.
            if (!response || !response.data) {
                clearNiagaraStationState();
                toast({
                    type: 'error',
                    description: `Could not load Niagara station data - your session may have expired, or you may not have access to this group`,
                    animation: 'fade down',
                    time: 8000,
                });
                return;
            }
            niagaraStations = response.data;
        } catch (e) {
            if (isStaleRequest(requestId)) {
                return;
            }
            clearNiagaraStationState();
            toast({
                type: 'error',
                description: `Could not load Niagara station data for this group`,
                animation: 'fade down',
                time: 8000,
            });
            return;
        }

        // Sorted on a copy - sorting in place would reorder the array the points are
        // built from below, making row order depend on when each JACE last posted.
        const timestamp = [...niagaraStations]
            .sort((a, b) => new Date(b.lastUpdatedAt) - new Date(a.lastUpdatedAt))[0]?.lastUpdatedAt;
        const newest = timestamp ? new Date(timestamp) : null;
        // new Date(undefined).toLocaleString() returns the *string* "Invalid Date",
        // which is truthy - so a group with no stations used to render
        // "Most Recent Timestamp: Invalid Date" instead of hiding the block.
        const formattedTimestamp =
            newest && !Number.isNaN(newest.getTime()) ? newest.toLocaleString() : null;

        let points;
        try {
            points = niagaraStations.flatMap((station) => {
                const mostRecentData = JSON.parse(station.mostRecentData);
                return (mostRecentData?.stationTree?.children || []).flatMap((stationTree) => {
                    const innerMostChildren = traverseStationTree(stationTree);
                    return innerMostChildren.map((child) => {
                        return {
                            hostId: station.hostId,
                            stationName: station.stationName,
                            stationLastUpdatedAt: station.lastUpdatedAt,
                            // Decoded for display as well as for the CSV, so the dropdown and
                            // the downloaded file never show different strings for one point.
                            // Safe because mapping submits on hostId + handle, never slotPath.
                            slotPath: decodeSlotPath(child.slotPath),
                            pointValue: child?.pointData?.value,
                            pointName: child?.name,
                            pointType: child?.type,
                            status: child?.pointData?.status,
                            units: child?.pointData?.units,
                            handle: child?.handle
                        }
                    });
                });
            });
        } catch (e) {
            if (isStaleRequest(requestId)) {
                return;
            }
            // A malformed most_recent_data blob is a data problem, not an auth one.
            // Telling the user their session expired would send them through a
            // re-login that cannot possibly help.
            clearNiagaraStationState();
            toast({
                type: 'error',
                description: `Niagara station data for this group could not be read`,
                animation: 'fade down',
                time: 8000,
            });
            return;
        }
        // Set together, after the parse: setting the timestamp earlier would render
        // the header block for one commit and then remove it on a malformed blob.
        setMostRecentTimestamp(formattedTimestamp);
        setNiagaraStationPoints(points);
    }

    const getMappedEquipmentPoints = async (groupId, requestId = null) => {
        if (!groupId) {
            return;
        }
        const mappedPoints = await equipmentService.getMappedEquipmentPoints(groupId);
        if (isStaleRequest(requestId)) {
            return;
        }
        // equipmentService returns {error, message} on an HTTP error and null on a
        // network failure. Normalising here keeps every consumer safe - the mapping
        // dropdown calls .find on this state too.
        const loaded = Array.isArray(mappedPoints);
        setMappedEquipmentPoints(loaded ? mappedPoints : []);
        // Tracked separately because [] cannot distinguish "this group has no
        // mappings" from "we could not find out". Without this the export would
        // produce a complete, plausible file with every row Mapped=false and no
        // indication anything went wrong - the same silent-success failure that
        // ruled out generating this CSV on the server.
        setMappedPointsLoadFailed(!loaded);
        if (!loaded) {
            toast({
                type: 'error',
                description: `Could not load existing point mappings - the Mapped column would be wrong, so the CSV download is unavailable until this loads`,
                animation: 'fade down',
                time: 8000,
            });
        }
    }

    const traverseStationTree = (branch, leafNodes = []) => {
        if (branch.children && branch.children.length > 0) {
            branch.children.forEach(child => traverseStationTree(child, leafNodes));
        } else {
            leafNodes.push(branch);
        }
        return leafNodes;
    };

    useEffect(() => {
        // Clearing lives here rather than in handleGroupChange so that clearing and
        // refetching are structurally inseparable. handleGroupChange is not the only
        // caller - the `location` effect above invokes it on every location change -
        // and a clear without a matching refetch would strip the button, the
        // timestamp and every Map Point button until a different group was chosen.
        const requestId = ++niagaraRequestRef.current;
        clearNiagaraStationState();
        setMappedEquipmentPoints([]);
        setMappedPointsLoadFailed(false);
        getNiagaraStations(groupId, requestId);
        getMappedEquipmentPoints(groupId, requestId);
    }, [groupId]);

    useEffect(() => {
        let tempOptions = [];
        setGroupOptions([]);
        groups.forEach(group => {
            tempOptions.push({
                key: group.id,
                value: group.groupId,
                text: group.name,
            });
        });
        setGroupOptions(tempOptions);
    }, [groups]);

    useEffect(() => {
        let tempOptions = [];
        setBuildingOptions([]);
        buildings.forEach(equipmentItem => {
            tempOptions.push({
                key: equipmentItem.id,
                value: equipmentItem.id,
                text: `(${equipmentItem.id}) ${equipmentItem.name}`,
            });
        });
        setBuildingOptions(tempOptions);
    }, [buildings]);

    useEffect(() => {
        let tempOptions = [];
        setEquipmentOptions([]);
        equipment.forEach(equipmentItem => {
            tempOptions.push({
                key: equipmentItem.id,
                value: equipmentItem.id,
                text: `(${equipmentItem.id}) ${equipmentItem.name}`,
            });
        });
        setEquipmentOptions(tempOptions);
    }, [equipment]);

    const handleRefresh = useCallback(() => {
        if (!buildingId && !equipmentId) {
            setEquipmentPoints([]);
        } else if (buildingId && equipmentId) {
            viewEquipmentPoints(null, equipmentId);
        } else {
            viewEquipmentPoints(buildingId, null);
        }
    }, [buildingId, equipmentId, viewEquipmentPoints]);

    useEffect(() => {
        handleRefresh();
    }, [buildingId, equipmentId, handleRefresh]);

    const handleGroupChange = groupId => {
        setGroupId(groupId);
        setBuildingOptions([]);
        setEquipmentOptions([]);
        getDataInspectorBuildings(groupId);
        setEquipmentPoints([]);
        // Niagara state is cleared by the [groupId] effect, alongside the refetch.
    };

    const handleBuildingChange = buildingId => {
        setBuildingId(buildingId);
        setEquipmentOptions([]);
        if (buildingId === '') {
            return;
        }
        getDataInspectorEquipment({
            buildingIds: [buildingId],
        });
    };

    const handleEquipmentChange = equipmentId => {
        setEquipmentId(equipmentId);
    };

    const handleNumericPointModalCancel = () => {
        setSelectedNumericPoint(null);
        setNumericPointTimestamp(moment().format('YYYY-MM-DDTHH:mm'));
        setNumericPointValue(0);
    };

    const handleNumericPointModalSubmit = async () => {
        if (numericPointTimestamp === "" || numericPointValue === "") {
            toast({
                type: 'error',
                description: `Numeric Point must have both a timestamp and a value`,
                animation: 'fade down',
                time: 5000,
            });
            setSelectedNumericPoint(null);
            return;
        }
        const result = await createNumericPoint(selectedNumericPoint, moment.utc(numericPointTimestamp).format(), numericPointValue);
        if (result > 0) {
            toast({
                type: 'success',
                description: `Equipment Numeric Point Value Assigned`,
                animation: 'fade down',
                time: 5000,
            });
            handleRefresh();
            setSelectedNumericPoint(null);
            setNumericPointTimestamp(moment().format('YYYY-MM-DDTHH:mm'));
            setNumericPointValue(0);
        } else {
            toast({
                type: 'error',
                description: `Equipment Numeric Point Value Assignment Failed`,
                animation: 'fade down',
                time: 5000,
            });
        }
    }

    const handlePointMappingSelect = (equipmentPoint) => {
        setSelectedPoint(equipmentPoint.pointId);
        if (equipmentPoint.stationHostId !== null && equipmentPoint.handle !== null) {
            const index = niagaraStationPoints.findIndex((point) => point.hostId === equipmentPoint.stationHostId && point.handle === equipmentPoint.handle);
            setSelectedNiagaraPointId(index);
        }
    }

    const handlePointMappingModalCancel = () => {
        setSelectedPoint(null);
        setSelectedNiagaraPointId(null);
    };

    const handleDownloadCsv = () => {
        let url = null;
        let link = null;
        try {
            const csv = buildNiagaraStationCsv(niagaraStationPoints, mappedEquipmentPoints);
            const filename = buildNiagaraCsvFilename(niagaraStationPoints, groupId, new Date());
            url = window.URL.createObjectURL(new Blob([csv], {type: 'text/csv;charset=utf-8'}));
            link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
        } catch (e) {
            toast({
                type: 'error',
                description: `Could not generate the CSV`,
                animation: 'fade down',
                time: 5000,
            });
        } finally {
            if (link) {
                link.remove();
            }
            if (url) {
                // Revoked on the next tick rather than synchronously: Safari and older
                // Firefox cancel a download whose object URL is revoked in the same
                // tick as the click.
                setTimeout(() => window.URL.revokeObjectURL(url), 0);
            }
        }
    };

    const handlePointMappingModalSubmit = async () => {
        if (selectedPoint === null || selectedNiagaraPointId === null) {
            return;
        }
        const result = await pointMappingService.mapPoint(selectedPoint, niagaraStationPoints[selectedNiagaraPointId].hostId, niagaraStationPoints[selectedNiagaraPointId].handle);
        if (result) {
            toast({
                type: 'success',
                description: `Equipment Point Mapping Updated`,
                animation: 'fade down',
                time: 5000,
            });
            handleRefresh();
            // Refetched here rather than in handleRefresh, which also fires on every
            // building and equipment change. Without this the Mapped column and the
            // dropdown's "Point Already Mapped" hint stay stale for the whole session.
            getMappedEquipmentPoints(groupId, niagaraRequestRef.current);
            setSelectedPoint(null);
            setSelectedNiagaraPointId(null);
        } else {
            toast({
                type: 'error',
                description: `Equipment Point Mapping Update Failed`,
                animation: 'fade down',
                time: 5000,
            });
        }
    }

    const handlePointMappingModalUnmap = async () => {
        const result = await pointMappingService.mapPoint(selectedPoint, null, null);
        if (result) {
            toast({
                type: 'success',
                description: `Equipment Point Unmapped`,
                animation: 'fade down',
                time: 5000,
            });
            handleRefresh();
            getMappedEquipmentPoints(groupId, niagaraRequestRef.current);
            setSelectedPoint(null);
            setSelectedNiagaraPointId(null);
        } else {
            toast({
                type: 'error',
                description: `Equipment Point Unmap Failed`,
                animation: 'fade down',
                time: 5000,
            });
        }
    }

    return (
        <div style={content}>
            <Modal
                closeIcon
                onClose={() => setSelectedNumericPoint(null)}
                open={selectedNumericPoint !== null}
                style={{textAlign: 'left'}}
                size={'tiny'}
            >
                <Modal.Header>Edit Numeric Point Value</Modal.Header>
                <Modal.Content>
                    <Form id={'numericPointForm'}>
                        {canSeeAllColumns ? <Form.Field>
                            <label>Equipment Information</label>
                            <div style={{whiteSpace: "pre-line"}}>
                                {equipmentPoints.find(x => x.pointId === selectedNumericPoint)?.equipmentTreeDisplayText}
                            </div>
                        </Form.Field> : null}
                            {canSeeAllColumns ? <Form.Field>
                            <label>Point Code</label>
                            <div>{equipmentPoints.find(x => x.pointId === selectedNumericPoint)?.pointCode}</div>
                        </Form.Field> : null }
                        <Form.Field>
                            <label>Point Description</label>
                            <div>{equipmentPoints.find(x => x.pointId === selectedNumericPoint)?.pointDescription}</div>
                        </Form.Field>
                        <Form.Field>
                            <label>Point Timestamp</label>
                            <Form.Input
                                type={'datetime-local'}
                                defaultValue={moment().format('YYYY-MM-DDTHH:mm')}
                                onChange={(e) => setNumericPointTimestamp(e.target.value)}
                            >
                            </Form.Input>
                        </Form.Field>
                        <Form.Field>
                            <label>Point Value</label>
                            <div>
                                <Form.Input
                                    name={'value'}
                                    type={'number'}
                                    value={numericPointValue}
                                    onChange={(e) => setNumericPointValue(e.target.value)}
                                />
                            </div>
                        </Form.Field>
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        type={'button'}
                        onClick={handleNumericPointModalCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        type={'button'}
                        onClick={handleNumericPointModalSubmit}
                    >
                        Submit
                    </Button>
                </Modal.Actions>
            </Modal>
            <Modal
                closeIcon
                onClose={() => setSelectedPoint(null)}
                open={selectedPoint !== null}
                style={{textAlign: 'left'}}
                size={"fullscreen"}
            >
                <Modal.Header>Edit Point Mapping - {equipmentPoints.find(x => x.pointId === selectedPoint)?.pointDescription}</Modal.Header>
                <Modal.Content>
                    <Form id={'pointMappingForm'}>
                        {canSeeAllColumns ? <Form.Field>
                            <label>Equipment Information</label>
                            <div style={{whiteSpace: "pre-line"}}>
                                {equipmentPoints.find(x => x.pointId === selectedPoint)?.equipmentTreeDisplayText}
                            </div>
                        </Form.Field> : null}
                        {canSeeAllColumns ? <Form.Field>
                            <label>Point Code</label>
                            <div>{equipmentPoints.find(x => x.pointId === selectedPoint)?.pointCode}</div>
                        </Form.Field> : null}
                        <Form.Field>
                            <label>Point Description</label>
                            <div>{equipmentPoints.find(x => x.pointId === selectedPoint)?.pointDescription}</div>
                        </Form.Field>
                        <Form.Field>
                            <label>Most Recent Timestamp</label>
                            <div>{mostRecentTimestamp}</div>
                        </Form.Field>
                        <Form.Field>
                            <label>Mapped Point (Station Name - Slot Path - Point Value)</label>
                            <div>
                                <Form.Dropdown
                                    name={'value'}
                                    type={'number'}
                                    defaultValue={selectedNiagaraPointId}
                                    onChange={(e, selected) => setSelectedNiagaraPointId(selected.value)}
                                    options={niagaraStationPoints.map((point, index) => {
                                        const isMapped = mappedEquipmentPoints.some((mappedPoint) => mappedPoint.stationHostId === point.hostId && mappedPoint.handle === point.handle);
                                        return {
                                            key: index,
                                            text: `${point.stationName} - ${point.slotPath} - ${point.pointValue}`,
                                            value: index,
                                            description: isMapped ? 'Point Already Mapped' : null
                                        }
                                    })}
                                    selection
                                    search
                                />
                            </div>
                        </Form.Field>
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        type={'button'}
                        onClick={handlePointMappingModalUnmap}
                    >
                        Unmap
                    </Button>
                    <Button
                        type={'button'}
                        onClick={handlePointMappingModalCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        type={'button'}
                        onClick={handlePointMappingModalSubmit}
                    >
                        Submit
                    </Button>
                </Modal.Actions>
            </Modal>
            <div>
                <Form>
                    <Form.Group style={{ display: 'flex' }}>
                        <div style={{ display: 'flex', flexBasis: '80%' }}>
                            <Form.Dropdown
                                width={4}
                                style={{height: '38px'}}
                                value={groupId}
                                label="Group Id"
                                placeholder="Group Id"
                                options={groupOptions}
                                onChange={(e, {value}) => handleGroupChange(value)}
                                selection
                                search
                                required
                            />
                            <Form.Dropdown
                                width={4}
                                style={{height: '38px'}}
                                value={buildingId}
                                label="Buildings"
                                placeholder="Building"
                                options={buildingOptions}
                                onChange={(e, {value}) => handleBuildingChange(value)}
                                selection
                                search
                                clearable
                                required
                            />
                            <Form.Dropdown
                                width={4}
                                style={{height: '38px'}}
                                value={equipmentId}
                                label="Equipment"
                                placeholder="Equipment"
                                options={equipmentOptions}
                                onChange={(e, {value}) => handleEquipmentChange(value)}
                                selection
                                search
                                clearable
                                required
                            />
                        </div>
                        {/* The timestamp and the download button are conditioned
                            independently: a station can carry points with no
                            lastUpdatedAt, and the button must not be unreachable
                            because of that. */}
                        {mostRecentTimestamp || niagaraStationPoints.length > 0 ? (
                            <div style={{display: 'flex', flexDirection: 'column', flexBasis: '20%'}}>
                                {mostRecentTimestamp ? (
                                    <>
                                        <div>Most Recent Timestamp:</div>
                                        <div>{mostRecentTimestamp}</div>
                                    </>
                                ) : null}
                                {niagaraStationPoints.length > 0 && !mappedPointsLoadFailed ? (
                                    <Button
                                        type={'button'}
                                        onClick={handleDownloadCsv}
                                        data-testid={'download-niagara-csv-button'}
                                        // alignSelf stops the button stretching to the
                                        // full width of this column flexbox, which on a
                                        // wide screen is far wider than the label needs.
                                        style={{marginTop: '0.5rem', alignSelf: 'flex-start'}}
                                    >
                                        Download CSV
                                    </Button>
                                ) : null}
                            </div>
                        ) : null}
                    </Form.Group>
                </Form>
            </div>
            <div style={{width: canSeeAllColumns ? '80vw' : '40vw'}}>
                <Grid data={equipmentPoints}>
                    {canSeeAllColumns ? <GridColumn field="equipmentId" title="Equipment Id"/> : null}
                    {canSeeAllColumns ? <GridColumn field="equipmentName" title="Equipment Name"/> : null}
                    {canSeeAllColumns ? <GridColumn field="pointCode" title="Point Code"/> : null}
                    <GridColumn field="pointDescription" title="Description"/>
                    {canSeeAllColumns ? <GridColumn field="pointType" title="Point Type"/> : null}
                    {canSeeAllColumns ? <GridColumn field="units" title="Units"/> : null}
                    <GridColumn field="value" title="Value" cell={props => (
                        <td>
                            {props.dataItem.pointType === 'NP' ? (
                                props.dataItem.value ?? 'null'
                            ) : (
                                props.dataItem.value === null ? (
                                    props.dataItem.stationHostId !== null && props.dataItem.handle !== null ? (
                                        "Point Mapped - No Data Received Yet"
                                    ) : (
                                        "Point Unmapped"
                                    )
                                ) : (
                                    props.dataItem.value
                                )
                            )}
                        </td>
                    )}/>
                    <GridColumn
                        cell={props => (
                            <td>
                                {(props.dataItem.pointType === 'NP' ? <Button
                                    type={'button'}
                                    onClick={() => {
                                        setSelectedNumericPoint(props.dataItem.pointId);
                                        setNumericPointValue(props.dataItem.value || 0);
                                    }}
                                >
                                    Set Value
                                </Button> : null)}
                                {(props.dataItem.pointType !== 'NP' ? <Button
                                    type={'button'}
                                    onClick={() => handlePointMappingSelect(props.dataItem)}
                                    disabled={niagaraStationPoints.length === 0}
                                >
                                    Map Point
                                </Button> : null)}
                            </td>
                        )}
                    />
                </Grid>
            </div>
            <div>
                <SemanticToastContainer position="bottom-center"/>
            </div>
        </div>
    );
}